import { ExportType } from '@/services/billingsService'

import { IClusterBilling } from './DTO/ClusterBillingDTO'
import { ComissionOption } from './DTO/ExportTaskDTO'
import { IAuditoriaFilters } from './DTO/IAuditoriaDTO'
import { IAlarm, IAlarmFilter } from './alarms'
import { AccountStatus, SignalQuality } from './meter'
import { PaginationDTO } from './table'
import { CommandType, TicketsStatus } from './tickets'

export enum ExportEmailType {
  withEmail,
  withoutEmail,
}

export interface IBaseExport {
  connectionId?: string
  requiresEmailNotification?: boolean
}

export interface IDownloadExportFilter {
  page: number
  pagesize: number
  dateTime?: string | null
  exportType?: ExportType | null
  sortOrder?: string | null
  email?: string | null
}

export interface IExportMassMemory extends IBaseExport {
  serial: string
  startDate: string | null
  endDate: string | null
}

export interface IExportFrozenData extends IBaseExport {
  serial: string
  startDate: string | null
  endDate: string | null
}

export interface IExportQee extends IBaseExport {
  serial: string
  startDate: string | null
  endDate: string | null
  page?: number | null
  pageSize?: number | null
}

export interface IExportInvoice extends IBaseExport {
  interval?: [string, string] | string[]
  startDate?: string
  endDate?: string
  stageCode?: string
  dateSent?: string
  status?: []
  manufacturers?: string[]
}

export interface IExportBilling extends IBaseExport {
  file: IClusterBilling[]
  dateTime: Date
  format: ExportType
  timeZone?: string
  utcOffsetMinutes?: number
}

export interface IExportMetersAlarms extends IBaseExport {
  sortBy: string | null
  sortOrder: string
  endDate: string | null
  startDate: string | null
  serialMeter: string
  description: string | null
}

export interface IExportMeters extends IBaseExport {
  sortOrder: string
  searchString: string | null
  page: number
  pageSize: number | null
  startDate?: string | null
  endDate?: string | null
  modemSignalQuality?: SignalQuality | string | null
  comissionOption?: ComissionOption | null
  manufacturers?: string[] | null
  accountantStatus?: AccountStatus
}

export interface IExportReportDaily extends IBaseExport {
  stage: string | null
  effectiveness?: string | null
  relayStatus: string | null
  serial?: string | null
  manufacturer: string | null
}
export interface ISMCStatistics extends IBaseExport {
  page: PaginationDTO<IAlarm>
  filter: IAlarmFilter
}

export interface IAlarmsDashboardExport extends IBaseExport {
  page: number
  pageSize: number | null
  types?: string[] | null
  visibleIds?: string[] | null
  startDate?: string | null
  endDate?: string | null
  wasRead?: boolean | null
  isCritical?: boolean | null
  searchString?: string | null
  sortBy?: string | null
}

export interface IQualityParametersExportFilters extends IBaseExport {
  startDate?: string | null
  endDate?: string | null
  voltage?: number | null
  serial?: string | null
  manufacturers?: string[]
  estados?: string[]
  cidades?: string[]
  categories?: string[] | null
  cities?: string[] | null
  states?: string[] | null
  installation?: string | null
  visibleIds?: string[] | null
}

export interface IExportSMCs extends IBaseExport {
  comissionOption: ComissionOption | null
  dimension?: string | null
  value?: string | null
  tagIds?: string | null
  smcModelIds?: string | null
  searchString?: string | null
  startDateTime?: string | null
  endDateTime?: string | null
  sortBy?: string | null
}

export interface IExportGateways extends IBaseExport {
  orderBy?: string | null
}

export interface IExportTickets extends IBaseExport {
  sortyBy?: string
  sortOrder?: string
  endDate?: string
  startDate?: string
  email?: string
  showSystem?: boolean
  serialMeter?: string
  commandTypes?: CommandType[]
  statusList?: TicketsStatus[]
  timeZone?: string
  utcOffsetMinutes?: number
}

export interface IExportServiceNotes extends IBaseExport {
  actionType?: string | null
  status?: string | null
  statusSap?: string | null
  company?: number | null
  meterSerial?: string | null
  startDate?: string
  initialDate?: string | null
  endDate?: string | null
  deadline?: string | null
  searchString?: string | null
}

export interface IExportMetersByEffectivenessStatus extends IBaseExport {
  date: string
  effectivenessType: string
  sortOrder?: string | null
  searchString?: string | null
  page?: number | null
  pageSize?: number | null
  startDateTime?: string | null
  endDateTime?: string | null
  comissioned?: boolean | null
  type?: string | null
  modemSignalQuality?: number | null
  visibleTags?: string[] | null
  status?: string | null
  manufacturers?: string[] | null
  networkTech?: string[] | null
  estados?: string[] | null
  cidades?: string[] | null
}

export interface IExportMetersByGroup extends IExportMeters {
  groupVisibleId: string
}

export interface IExportGroupQualityParameters extends IBaseExport {
  groupVisibleId: string
  reference: string
}

export interface IExportGroupLatestQualityParameters extends IBaseExport {
  groupVisibleId: string
  searchString?: string | null
}

export interface IExportGroupDicFicMonthly extends IBaseExport {
  groupVisibleId: string
  month: number
  year: number
}

export interface IExportAbruptVariationCustomReport extends IBaseExport {
  startDate?: string | null
  endDate?: string | null
  superintendences?: string[] | null
  regionals?: string[] | null
  sectionals?: string[] | null
  cities?: string[] | null
  groupings?: number[] | null
  companyGroups?: string[] | null
}

export interface IExportIagdRanking extends IBaseExport {
  riskLevel?: string
  flagType?: string
  scoreMin?: number
  scoreMax?: number
  dateFrom?: string
  dateTo?: string
}

export interface IExportIagdCompleteness extends IBaseExport {
  dateFrom?: string
  dateTo?: string
  search?: string
  status?: string
  cluster?: string
}

export interface IExportCceeSubmissions extends IBaseExport {
  startDate?: string | null
  endDate?: string | null
}

export interface IExportCceePerformance extends IBaseExport {
  startDate?: string | null
  endDate?: string | null
}

export interface IExportCceeAlerts extends IBaseExport {
  startDate?: string | null
  endDate?: string | null
  priority?: string | null
  serial?: string | null
  alertType?: number | null
}

export interface IExportCceeAnomalies extends IBaseExport {
  startDate?: string | null
  endDate?: string | null
}

export interface IExportCceeSendStatus extends IBaseExport {
  startDate?: string | null
  endDate?: string | null
  status?: string | null
  serial?: string | null
  recoveryStatus?: string | null
}

export interface IExportCceeGeneratedFile extends IBaseExport {
  exportRoute: string
  referenceDate: string
}

export interface IExportCceeSendStatus extends IBaseExport {
  startDate?: string | null
  endDate?: string | null
  status?: string | null
  serial?: string | null
}

export interface IExportCceeReadings extends IBaseExport {
  serial?: string | null
  startDate?: string | null
  endDate?: string | null
  status?: string | null
}
